import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import warningIcon from '../../assets/icons/warning.svg';

const GuidelinesModal = ({ isOpen, onClose, onSave, initialText, showSaveButton = true }) => {
  const [text, setText] = useState(initialText);
  const { id } = useParams();

  useEffect(() => {
    if (isOpen) {
      setText(initialText);
    }
  }, [isOpen, initialText]);
  
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const handleSave = () => {
    onSave(text);
    onClose();
  };

  const handleDuplicateEvaluation = () => {
    localStorage.setItem(`duplicate_${id}`, 'true');
    localStorage.setItem(`original_id`, id);
    window.open('/evaluate', '_blank');
  };

  const isCollapsed = !showSaveButton && text === '';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className={`w-full max-w-[800px] max-h-[1080px] h-[calc(100vh-160px)] overflow-hidden gap-0
          ${isCollapsed ? '!max-h-[398px]' : ''}`}
        style={{ 
          animationDuration: '0ms !important', 
          transitionDuration: '0ms !important',
          animation: 'none !important',
          transition: 'none !important'
        }}
      >
        <style>
        {`.custom-default-scrollbar::-webkit-scrollbar {
                -webkit-appearance: none;
                height: 2px;
                max-height: 28px;
                width: 2px;
                padding-right: 10px;
                padding-left: 10px;
            }
            .custom-default-scrollbar::-webkit-scrollbar-track {
              background-color: rgba(0, 0, 0, .05);
            }
            .custom-default-scrollbar::-webkit-scrollbar-thumb {
                border-radius: 2px;
                background-color: rgba(0, 0, 0, .2);
                padding-right: 10px;
                padding-left: 10px;
        }`}
        </style>
        <DialogHeader className="!space-y-0 h-[32px] p-0 mb-6">
          <DialogTitle className="text-lg font-medium text-[#020817]">
            Guidelines (optional)
          </DialogTitle>
          <DialogDescription className="text-medium text-[#6B7280] mt-2">
            Context, Style Guide, Glossary etc.
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-auto">
          <div className="pt-3 md:pt-6 overflow-auto h-[calc(76vh-96px)] mobile:p-3 mobile:h-[calc(76vh-140px)]">
            {showSaveButton ? (
              <textarea
                className={`w-full h-full text-sm text-[#020817] font-mono p-4 rounded-md border resize-none custom-default-scrollbar focus-visible:outline-none focus-visible:ring-0`}
                style={{minHeight: '300px'}}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={`CONTEXT:\nTranslate a corporate merger agreement to Spanish for legal teams and executives in Latin America.\n\nSTYLE GUIDE:\nMaintain formal legal tone. Use precise language and established legal terms in third person.\n\nGLOSSARY:  \nMerger = fusión empresarial\nDue diligence = diligencia debida\nBreach of contract = incumplimiento contractual\n\nQUALITY REQUIREMENTS:\nEnsure accuracy in legal terms, amounts, and entities. Maintain enforceability and precise legal meaning.\n\nFORMATTING REQUIREMENTS:\nKeep article structure and defined terms' capitalization.\n\nCULTURAL CONSIDERATIONS:\nAdapt for Mexican law and local legal systems.`}
              />
            ) : (
              <>
                <pre
                  className={`whitespace-pre-wrap font-mono text-xs text-[#6B7280] 
                  bg-[#F1F5F9] p-4 rounded-md mobile:p-2 mobile:text-xs w-full
                  custom-default-scrollbar sm:max-h-[320px] max-h-[345px]
                  min-h-[300px] overflow-y-auto
                  ${isCollapsed ? '!max-h-[65px] min-h-[65px] !overflow-y-hidden' : ''}`}
                >
                  {text || 'CONTEXT: \nTranslate a corporate merger agreement to Spanish for legal teams and executives in...'}
                </pre>
                {isCollapsed && (
                  <div className="flex items-start bg-[#FEF08A] border-none text-[#020817] rounded-[8px] p-4 mt-6 text-sm">
                    <img src={warningIcon} alt="Warning icon" className="mr-2 flex-shrink-0" />
                    Nothing was added to the guidelines. You can duplicate the evaluation, add context, a style guide, a glossary, etc., and start the evaluation again.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        <div className="flex justify-end pt-6 gap-2">
          <Button className="h-10" variant="outline" onClick={onClose}>Cancel</Button>
          {!showSaveButton && (
            <Button
              onClick={handleDuplicateEvaluation}
              className="h-10 px-4 py-2 bg-[#020817] text-white rounded-md font-medium hover:bg-[#29303D] transition"
            >
              Duplicate Evaluation
            </Button>
            )}
          {showSaveButton && (
            <Button onClick={handleSave}>Save</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default GuidelinesModal;